/**
 * Weather Stations Wind Map Service
 *
 * This module provides a custom hook for managing weather station wind data visualization on a map.
 * It handles wind speed and direction data fetching, state management, and map interactions.
 *
 * Key Features:
 * 1. Data Management:
 *    - Fetches wind speed and direction data
 *    - Manages station metadata
 *    - Handles data updates with QC values
 *
 * 2. Map Integration:
 *    - Integrates with map libraries
 *    - Manages wind barb markers
 *    - Handles map interactions
 *
 * 3. State Management:
 *    - Tracks loading states
 *    - Manages error states
 *    - Handles data refresh
 *
 * 4. Performance:
 *    - Optimized data fetching
 *    - Efficient state updates
 *    - Caching mechanisms
 */

import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { stationMetaData } from "../utils/rawData";
import { useEffect } from "react";

// API configuration
const API_BASE_URL = "https://client-api.silanga.ca/aws";
const ECCC_API_BASE_URL = "https://client-api.silanga.ca/ECCC";
const NTI_API_BASE_URL = "https://www.smartatlantic.ca/erddap/tabledap";
const API_KEY = process.env.REACT_APP_API_TOKEN;

/**
 * Converts wind speed from m/s to knots
 * @param {number} speedMs - Wind speed in m/s
 * @returns {number} Wind speed in knots
 */
const msToKnots = (speedMs) => {
  return speedMs * 1.94384;
};

/**
 * Rounds wind speed to nearest 5 knots for wind barb selection
 * @param {number} speedKnots - Wind speed in knots
 * @returns {number} Rounded wind speed
 */
const roundToNearestFive = (speedKnots) => {
  return Math.round(speedKnots / 5) * 5;
};

/**
 * Fetches wind data from NTI SmartAtlantic API
 * @param {string} stationId - The station ID (UID)
 * @returns {Promise<Object>} - Transformed wind data
 */
const fetchNTIWindData = async (stationId) => {
  try {
    const endpoint = `${NTI_API_BASE_URL}/${stationId}.jsonlKVP?&time%3E=max(time)-1hour&orderByDescending(%22time%22)`;

    const response = await axios.get(endpoint);

    const responseData = response.data.trim().split("\n");
    const firstDataObj = JSON.parse(responseData[0]);

    /**
     * Maps NTI QC value to application QC value
     * NTI QC values:
     * -1: Missing/Invalid - Data is missing, invalid, or not evaluated
     *  0: Not Checked - Data has not been QC-checked or status is unknown
     *  1: Pass/Good - Data has passed QC checks and is considered good/valid
     *
     * App QC values:
     * 100: Good data
     *   0: Error/bad data
     */
    const mapQCValue = (qcStatus) => {
      if (qcStatus === 1) return 100; // Pass/Good -> 100 (Good)
      if (qcStatus === -1) return -10; // Missing/Invalid -> -10
      return undefined; // Not Checked (0)
    };

    const timestamp = firstDataObj.time;

    return {
      timestamp,
      elements: {
        wind_speed: {
          value:
            firstDataObj.wind_speed_avg !== undefined
              ? firstDataObj.wind_speed_avg
              : null,
          qc_value:
            firstDataObj.qc_status !== undefined
              ? mapQCValue(firstDataObj.qc_status)
              : undefined,
        },
        wind_direction: {
          value:
            firstDataObj.wind_dir_avg !== undefined
              ? firstDataObj.wind_dir_avg
              : null,
          qc_value:
            firstDataObj.qc_status !== undefined
              ? mapQCValue(firstDataObj.qc_status)
              : undefined,
        },
      },
    };
  } catch (error) {
    console.error(
      `Error fetching NTI wind data for station ${stationId}:`,
      error
    );
    return null;
  }
};

/**
 * Fetches wind data for a specific station
 * @param {Object} station - The station configuration
 * @returns {Promise<Object|null>} The wind data or null if fetch fails
 */
const fetchWindData = async (station) => {
  try {
    if (station.type === "NTI_Weather_Station") {
      return await fetchNTIWindData(station.id);
    } else {
      const baseUrl =
        station.type === "ECCC_Weather_Station"
          ? ECCC_API_BASE_URL
          : API_BASE_URL;
      const response = await axios.get(baseUrl, {
        params: { uid: station.id },
        headers: { "x-api-key": API_KEY },
      });
      return response.data[0];
    }
  } catch (error) {
    console.error(`Error fetching wind data for station ${station.id}:`, error);
    return null;
  }
};

/**
 * Custom Hook: useWeatherStationsWindMap
 *
 * This hook provides weather station wind map functionality:
 * 1. Initializes map state
 * 2. Fetches wind data (speed and direction)
 * 3. Manages wind barb markers
 * 4. Handles user interactions
 *
 * Usage:
 * const { stations, loading, error } = useWeatherStationsWindMap({ enabled: true });
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether the query should be enabled
 * @returns {Object} Query result with weather stations wind data
 */
export const useWeatherStationsWindMap = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();

  /**
   * Fetches and updates wind data for all stations
   * Uses optimistic updates to show loading states immediately
   * @returns {Promise<Array>} Updated station data with wind information
   */
  const fetchWeatherStationsWind = async () => {
    // Initialize with loading state for all stations
    const initialStations = stationMetaData.map((station) => ({
      ...station,
      lat: station.default_latitude,
      lon: station.default_longitude,
      windSpeed: null,
      windDirection: null,
      windSpeedKnots: null,
      windBarbIcon: "0",
      info: "Loading...",
      hasError: false,
      isLoading: true,
    }));

    // Set initial state
    queryClient.setQueryData("weatherStationsWind", initialStations);

    // Fetch data for each station and update individually
    const promises = stationMetaData.map(async (station, index) => {
      try {
        // Skip API calls for non-operational stations
        if (station.status !== "Operational") {
          queryClient.setQueryData("weatherStationsWind", (oldData) => {
            if (!oldData) return oldData;
            const newData = [...oldData];
            newData[index] = {
              ...station,
              lat: station.default_latitude,
              lon: station.default_longitude,
              windSpeed: null,
              windDirection: null,
              windSpeedKnots: null,
              windBarbIcon: "0",
              info: "Non - Operational",
              hasError: false,
            };
            return newData;
          });
          return;
        }

        const data = await fetchWindData(station);

        // Update single station data as it arrives
        queryClient.setQueryData("weatherStationsWind", (oldData) => {
          if (!oldData) return oldData;
          const newData = [...oldData];

          if (data && data.elements) {
            const windSpeed = data.elements.wind_speed?.value;
            const windDirection = data.elements.wind_direction?.value;
            const windSpeedQc = data.elements.wind_speed?.qc_value;
            const windDirectionQc = data.elements.wind_direction?.qc_value;

            // Convert wind speed to knots and round to nearest 5
            let windSpeedKnots = null;
            let windBarbIcon = "0";

            if (windSpeed !== null && windSpeed !== undefined) {
              windSpeedKnots = msToKnots(windSpeed);
              const roundedSpeed = roundToNearestFive(windSpeedKnots);
              // Cap at 190 knots (max wind barb icon available)
              windBarbIcon = Math.min(roundedSpeed, 190).toString();
            }

            // Check if data is valid based on QC values
            const isWindSpeedValid =
              windSpeedQc === undefined || windSpeedQc === 100;
            const isWindDirectionValid =
              windDirectionQc === undefined || windDirectionQc === 100;
            const hasValidWindData =
              windSpeed !== null &&
              windSpeed !== undefined &&
              windDirection !== null &&
              windDirection !== undefined &&
              isWindSpeedValid &&
              isWindDirectionValid;

            newData[index] = {
              ...station,
              lat: station.default_latitude,
              lon: station.default_longitude,
              windSpeed: windSpeed,
              windDirection: windDirection,
              windSpeedKnots: windSpeedKnots,
              windBarbIcon: hasValidWindData ? windBarbIcon : "0",
              windSpeedQc: windSpeedQc,
              windDirectionQc: windDirectionQc,
              info: hasValidWindData
                ? `${Math.round(windSpeedKnots)} kts @ ${Math.round(
                    windDirection
                  )}°`
                : windSpeed !== null && windSpeed !== undefined
                ? "Invalid Data"
                : "N/A",
              hasError:
                !hasValidWindData &&
                (windSpeed !== null || windDirection !== null),
            };
          } else {
            newData[index] = {
              ...station,
              lat: station.default_latitude,
              lon: station.default_longitude,
              windSpeed: null,
              windDirection: null,
              windSpeedKnots: null,
              windBarbIcon: "0",
              info: "Non - Operational",
              hasError: true,
            };
          }
          return newData;
        });
      } catch (error) {
        console.error(
          `Error fetching wind data for station ${station.id}:`,
          error
        );
      }
    });

    // Wait for all promises to complete
    await Promise.all(promises);
    return queryClient.getQueryData("weatherStationsWind");
  };

  // Set up the query with React Query
  const query = useQuery("weatherStationsWind", fetchWeatherStationsWind, {
    enabled: enabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
  });

  /**
   * Sets up automatic hourly data refresh
   * Refreshes data at 3 minutes past each hour
   */
  useEffect(() => {
    if (!enabled) return;

    const scheduleNextRefetch = () => {
      const now = new Date();
      const nextRefetchTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours() + 1,
        3,
        0
      );

      const timeUntilNextRefetch = nextRefetchTime.getTime() - now.getTime();
      return setTimeout(async () => {
        await fetchWeatherStationsWind().then((newData) => {
          queryClient.setQueryData("weatherStationsWind", newData);
        });
        scheduleNextRefetch();
      }, timeUntilNextRefetch);
    };

    const timeoutId = scheduleNextRefetch();

    return () => clearTimeout(timeoutId);
  }, [queryClient, enabled]);

  /**
   * Manually refreshes all weather station wind data
   * @returns {Promise<boolean>} True when refresh is complete
   */
  const refetchWeatherStationsWind = async () => {
    const newData = await fetchWeatherStationsWind();
    queryClient.setQueryData("weatherStationsWind", newData);
    return true;
  };

  return { ...query, refetchWeatherStationsWind };
};
